import { useContext } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Error400, Error403, Error404, Error500 } from './ErrorPages';
import { ApplicationContext } from '../../../../shared/source/context/context';
import {
    is400Error,
    is403Error,
    is404Error,
    is500Error,
    isNetworkError,
} from '../../../../shared/source/error-code/error-code';
import { PrimaryActionButton } from '../../design/components/button/PrimaryAction';
import { ErrorAlert } from '../../design/components/errors/ErrorAlert';
import { ErrorPage } from '../../design/components/errors/ErrorPage';

import type { Translate, TranslationKey } from '../../../../shared/source/internationalization/translation';
import type { Logger } from '../../../../shared/source/logger/logger';
import type { FunctionComponent, JSX, PropsWithChildren, ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';

export const renderErrorCodePage = (translate: Translate): ((props: FallbackProps) => ReactNode) => {
    const ErrorCodePage = ({ error }: FallbackProps): JSX.Element => {
        if (is400Error(error)) {
            return <Error400 />;
        }

        if (is403Error(error)) {
            return <Error403 />;
        }

        if (is404Error(error)) {
            return <Error404 />;
        }

        if (is500Error(error)) {
            return <Error500 />;
        }

        if (isNetworkError(error)) {
            return (
                <ErrorPage message={translate('errors.network.message')} title={translate('errors.network.title')}>
                    <PrimaryActionButton
                        label={translate('errors.common.action')}
                        onClick={() => globalThis.location.reload()}
                    />
                </ErrorPage>
            );
        }

        return (
            <ErrorPage message={translate('errors.common.message')} title={translate('errors.common.title')}>
                <PrimaryActionButton
                    label={translate('errors.common.action')}
                    onClick={() => globalThis.location.reload()}
                />
            </ErrorPage>
        );
    };

    return ErrorCodePage;
};

const onPageErrorMessageBoundaryError = (error: Error, logger: Logger): void => {
    if (error.message?.startsWith('Failed to fetch dynamically imported module') ?? false) {
        logger.warn('reloading without cache', globalThis.location.href);
        fetch(globalThis.location.href, { cache: 'no-cache' }).then(() => globalThis.location.reload());
    }
};

export const PageErrorBoundary: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const {
        applicationState: { location },
        logger,
        translate,
    } = useContext(ApplicationContext);
    const { pathname } = location;

    return (
        <ErrorBoundary
            fallbackRender={renderErrorCodePage(translate)}
            resetKeys={[pathname]}
            onError={(error) => {
                onPageErrorMessageBoundaryError(error, logger);
            }}
        >
            {children}
        </ErrorBoundary>
    );
};

export const renderComponentErrorMessage = (message: string): ((props: FallbackProps) => ReactNode) => {
    const Wrapped = (): JSX.Element => <ErrorAlert message={message} />;
    Wrapped.displayName = 'ComponentErrorMessage';

    return Wrapped;
};

type ComponentErrorBoundaryProps = PropsWithChildren<{ readonly message?: TranslationKey }>;

export const ComponentErrorBoundary: FunctionComponent<ComponentErrorBoundaryProps> = ({ children, message }) => {
    const { translate } = useContext(ApplicationContext);
    const translatedErrorMessage = translate(message ?? 'errors.common.message');

    return (
        <ErrorBoundary fallbackRender={renderComponentErrorMessage(translatedErrorMessage)}>{children}</ErrorBoundary>
    );
};
